import { analysisDayFromOptions, analysisTypeOptions } from '@/components/Studies/Groups/Analysis/Analysis.util';
import { AnalysisIssueCounter } from '@/components/Studies/Groups/Analysis/AnalysisIssueCounter';
import { AnalysisSetup } from '@/components/Studies/Groups/Analysis/AnalysisSetup';
import Button from '@/components/UI/Button';
import { Dropdown } from '@/components/UI/Dropdown';
import Icon from '@/components/UI/Icon';
import { errorToast, successToast } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import { AnalysisIssue, AnalysisOptions, TrackingFrom } from '@/model/Analysis.model.ts';
import type { Preset } from '@/model/Preset.model';
import type { StudyApiId } from '@/model/Study.model';
import { type FC, useMemo, useState } from 'react';
import { useModalAction } from '@/utils/modal';
import Http from '@/support/http';
import { RawAxiosResponseHeaders } from 'axios';
import { RouteService } from '@/support/RouteService';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useAnalysis } from '@/utils/analysis/useAnalysis';

interface AnalysisToolbarProps {
  studyApiId: StudyApiId;
  preset: Preset;
  hasAccess: boolean;
}

interface TableTitleProps {
  options: AnalysisOptions | undefined;
  measurements: Record<string, { value: string; label: string }>;
}

const TableTitle: FC<TableTitleProps> = ({ options, measurements }) => {
  return (
    <div>
      <div className="f5 near-black">Summary Table</div>
      {_notNil(options) && _notNil(options.day) && (
        <div className="f6 mt1">
          <span>{analysisTypeOptions[options.type].label}</span>
          {Object.prototype.hasOwnProperty.call(measurements, options.measurement as string) && (
            <>
              <span className="pl1 pr1">/</span>
              <span>{measurements[options.measurement as string].label}</span>
            </>
          )}
          {options.type !== 'Survival' && options.type !== 'Tolerance' && options.type !== 'ToleranceSurvival' && (
            <>
              <span className="pl1 pr1">/</span>
              <span>Day {options.day}</span>
            </>
          )}
          {Object.prototype.hasOwnProperty.call(analysisDayFromOptions, options.day_from as TrackingFrom) && (
            <span className="pl1 pr1">( {analysisDayFromOptions[options.day_from as TrackingFrom].label} )</span>
          )}
        </div>
      )}
    </div>
  );
};

export const AnalysisToolbar: FC<AnalysisToolbarProps> = ({ studyApiId, preset, hasAccess }) => {
  const { options, config, issues, exportSnapshotApiId, updateAnalysisOptions, updateAnalysisIssues } = useAnalysis();
  const { openModal } = useModalAction();
  const [exporting, setExporting] = useState<boolean>(false);
  const isMounted = useMountedState();

  const handleDownloadFile = (data: BlobPart, headers: Partial<RawAxiosResponseHeaders>) => {
    const fileName: string = headers['file-name'] as string;
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      if (_notNil(exportSnapshotApiId)) {
        const { data, headers } = await Http({
          method: 'get',
          url: RouteService.legacyApi({
            apiRoute: 'snapshot.export',
            path: { studyApiId, exportSnapshotApiId },
          }).url.href,
          responseType: 'blob',
        });
        if (isMounted()) {
          handleDownloadFile(data, headers);
          successToast(`Successfully exported analysis.`);
          setExporting(false);
        }
      }
    } catch (error) {
      errorToast('Could not export analysis. Please try again later');
      setExporting(false);
    }
  };
  const handleSubmit = async (data: AnalysisOptions) => {
    try {
      await updateAnalysisOptions(data, studyApiId);
      if (isMounted()) {
        if (data.set_as_default) {
          successToast('Analysis options saved as default');
        }
      }
    } catch (error) {
      errorToast('Could not submit analysis options. Please try again later');
    }
  };

  const measurements = useMemo(
    () =>
      (preset?.calculations ?? []).reduce(
        (acc, calculation) => ({
          ...acc,
          [calculation.id]: { value: calculation.id, label: calculation.name },
        }),
        {}
      ),
    [preset]
  );
  const { noConfig, optionsNotSet, optionsButtonDisabled } = useMemo(() => {
    const errorIssues = (issues ?? []).some((i) => i.level === 'Error');
    const noConfig = _isNil(config);
    const optionsNotSet = _isNil(options?.day);
    return { noConfig, optionsNotSet, optionsButtonDisabled: errorIssues || noConfig };
  }, [issues, config, options]);
  return (
    <div
      className="ph4 pv3 bg-white bb b--moon-gray flex justify-between items-center"
      data-test-component="AnalysisToolbar"
      data-test-element="container"
    >
      <div>
        <TableTitle options={options} measurements={measurements} />
      </div>
      <div className="flex items-center justify-end">
        <AnalysisIssueCounter
          issues={issues ?? []}
          onClick={() =>
            openModal('ANALYSIS_ISSUES', {
              studyApiId: studyApiId,
              onAnalysisIssuesUpdate: (updatedIssues: Array<AnalysisIssue>) => updateAnalysisIssues(updatedIssues),
            })
          }
        />
        <div className="pr3">
          <Dropdown
            open={_notNil(config) && !Number.isInteger(options?.day)}
            disabled={optionsButtonDisabled}
            renderMenu={({ toggleDropdownMenu }) => (
              <AnalysisSetup
                measurements={measurements}
                onChange={(data) => {
                  toggleDropdownMenu(false);
                  handleSubmit(data);
                }}
                hasAccess={hasAccess}
              />
            )}
          >
            <Button plain disabled={optionsButtonDisabled} loading={noConfig} testId="analysis-options-button">
              <span className="flex justify-between">
                <span>Options</span>
                <span className="pl2">
                  <Icon icon="down_triangle" width="8" height="8" className="right-1" />
                </span>
              </span>
            </Button>
          </Dropdown>
        </div>
        <div>
          <Button disabled={optionsButtonDisabled || optionsNotSet} loading={exporting} onClick={handleExport}>
            Export
          </Button>
        </div>
      </div>
    </div>
  );
};
